import { Order } from "../../../core/models/entities/Order";
import { Role } from "../../../core/models/entities/Role";
import { enviroment } from "../../../enviroment";
import { agreedPriceApplies } from "./subcomponents/OrderLineData";

// Admin price adjustment (flag REACT_APP_ADMIN_PRICE_ADJUSTMENT_ENABLED):
// pure display rules shared by the order list row, the read-only order
// confirmation view and the order details. No data calls anywhere here -
// everything is derived from the order payload already on screen.

// Query parameter that opens the "Price adjustments" panel on the order
// details right away (the list badge and the confirmation notice link there).
export const PRICE_ADJUSTMENTS_PARAM = "priceAdjustments";

export function priceAdjustmentsDetailsUrl(order: Order): string {
  return (
    `/order/details?orderId=${order.id}&orgId=${order.organization.id}` +
    `&${PRICE_ADJUSTMENTS_PARAM}=1`
  );
}

// A line carrying an agreed unit price. Today the API attaches
// agreedUnitPrice on the ADMIN read only (GetOrderByAdmin; the OrderDto
// lines are built by the hand-written OrderConverter, which never copies
// it, and the org-scoped GetOrder / GetOrders never patch it in). The
// rule here is payload-driven on purpose: the moment the org read carries
// the field for vendor/admin callers (API follow-up, see the PR's
// "API-forudsaetning"), the customer's own views light up without an FE
// change.
export function orderHasAgreedPrices(order: Order): boolean {
  return (order.lines ?? []).some(
    (line) =>
      line.product?.agreedUnitPrice !== undefined &&
      line.product?.agreedUnitPrice !== null,
  );
}

// "Was adjusted": the payload says so (priceAdjustment summary), or the
// lines still carry an agreement. Known gap: an adjustment that removed
// every agreement again leaves a payload without the summary (today: the
// list rows and the org-scoped reads) without a badge/notice.
export function orderIsPriceAdjusted(
  order: Order,
  enabled: boolean = enviroment.adminPriceAdjustmentEnabled,
): boolean {
  if (!enabled) return false;
  return !!order.priceAdjustment || orderHasAgreedPrices(order);
}

export interface OrderTotals {
  subTotal: number;
  vat: number;
  total: number;
}

// The read-only order views (/order/confirm - the admin's and the paying
// vendor's landing page from the order list - and /order/details for a
// vendor on its own prices) sum their totals client-side from the stored
// line amounts (VendorPrice/CustomerPrice x qty), which an adjustment never
// changes - so they kept showing the pre-adjustment totals no matter how
// fresh the fetch was. For a placed order that WAS adjusted (the
// priceAdjustment summary, or lines carrying an agreed price), read on the
// vendor basis (admin, or a vendor on its own prices - the same rule the
// rows use to strike our price through), the API's stored totals
// (Orders.SubTotal/VAT/Total, rewritten by every adjustment) are the fact:
// they are what the new payment link charges. Returns undefined - "compute
// as before" - for drafts, for the customer-price view, for untouched
// orders, when the stored totals are missing, and always while the FE flag
// is off (byte-identical totals).
export function placedOrderTotalsOverride(
  order: Order,
  role: Role | undefined,
  showCustomerPrices: boolean,
  enabled: boolean = enviroment.adminPriceAdjustmentEnabled,
): OrderTotals | undefined {
  if (!enabled) return undefined;
  if (!agreedPriceApplies(role, showCustomerPrices, enabled)) return undefined;
  if (!orderIsPriceAdjusted(order, enabled)) return undefined;
  if (!order.subTotal || !order.total) return undefined;
  return {
    subTotal: order.subTotal,
    vat: order.vat ?? 0,
    total: order.total,
  };
}
